import {
  type LocalStorageClient,
  localStorageClient,
} from 'clients/local-storage'
import {
  DEFAULT_PROJECT_FILTERS,
  type ProjectFilters,
} from 'features/projects/utils/project-filters'

const STORAGE_KEY = 'projects:filters'

/**
 * Merge the persisted value over the defaults, type-guarding each field so a
 * schema change (new field) or stale payload never yields a broken object.
 */
const normalizeProjectFilters = (value: unknown): ProjectFilters => {
  if (typeof value !== 'object' || value === null)
    return DEFAULT_PROJECT_FILTERS
  const c = value as Partial<ProjectFilters>
  return {
    internal: ['any', 'internal', 'external'].includes(c.internal as string)
      ? (c.internal as ProjectFilters['internal'])
      : DEFAULT_PROJECT_FILTERS.internal,
    archived: ['any', 'active', 'archived'].includes(c.archived as string)
      ? (c.archived as ProjectFilters['archived'])
      : DEFAULT_PROJECT_FILTERS.archived,
    data_types: Array.isArray(c.data_types)
      ? c.data_types
      : DEFAULT_PROJECT_FILTERS.data_types,
    usage: Array.isArray(c.usage) ? c.usage : DEFAULT_PROJECT_FILTERS.usage,
    states: Array.isArray(c.states) ? c.states : DEFAULT_PROJECT_FILTERS.states,
    n_brands: c.n_brands ?? null,
    n_products: c.n_products ?? null,
    n_sources: c.n_sources ?? null,
    n_datasets: c.n_datasets ?? null,
    n_posts: c.n_posts ?? null,
    n_ratings: c.n_ratings ?? null,
    last_update: c.last_update ?? DEFAULT_PROJECT_FILTERS.last_update,
    last_record_date:
      c.last_record_date ?? DEFAULT_PROJECT_FILTERS.last_record_date,
  }
}

export class ProjectFiltersStorageService {
  constructor(private readonly storageClient: LocalStorageClient) {}

  load(): ProjectFilters {
    return normalizeProjectFilters(
      this.storageClient.getJson(STORAGE_KEY, DEFAULT_PROJECT_FILTERS),
    )
  }

  save(filters: ProjectFilters): void {
    this.storageClient.setJson(STORAGE_KEY, normalizeProjectFilters(filters))
  }
}

/** Exported so auth forms can preserve this key across their localStorage wipe. */
export const PROJECT_FILTERS_STORAGE_KEY = STORAGE_KEY

export const projectFiltersStorageService = new ProjectFiltersStorageService(
  localStorageClient,
)
